import React from 'react';
import styled from 'styled-components';
import { Box, Button, Text } from '@rsos/base-components';
import getLanguageDisplayNames from './getLanguageDisplayNames';

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const Label = styled(Text).attrs({
  size: 'small',
  color: 'secondaryText',
  fontWeight: 'semibold',
})``;

const LanguagePillSelectorWrapper = styled(Box).attrs({
  horizontal: true,
  width: '100%',
})`
  gap: 8px;
`;

const LanguagePillSelectorButton = styled(Button)<{ isActive: boolean }>`
  padding: 4px 10px;
  height: auto;
  flex: 1;
  width: 100%;
  background-color: ${({ isActive, theme }) =>
    isActive ? theme.colors.highlights : 'transparent'};
  color: ${({ isActive, theme }) =>
    isActive ? theme.colors.primaryText : theme.colors.secondaryText};
`;

const TooltipWrapper = styled.div`
  position: relative;
  display: flex;
  flex: 1;
`;

const TooltipContent = styled(Box).attrs({
  color: 'background',
  bg: 'primaryText',
  borderRadius: '5px',
  p: '10px',
})`
  position: absolute;
  visibility: hidden;
  white-space: nowrap;
  z-index: 10;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  width: max-content;
  pointer-events: none;
`;

const TooltipTrigger = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  &:hover ${TooltipContent} {
    visibility: visible;
  }
`;

interface LanguagePillSelectorV2Props {
  selectedLanguage?: 'original' | 'translated';
  detectedLanguages?: string[];
  onChange?: (transcriptVersion: 'original' | 'translated') => void;
}

const LanguagePillSelectorV2 = ({
  selectedLanguage = 'original',
  detectedLanguages = [],
  onChange,
}: LanguagePillSelectorV2Props) => {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleChange = onChange || (() => {});
  const languagesText =
    detectedLanguages.length > 0
      ? getLanguageDisplayNames(detectedLanguages).join(', ')
      : 'No languages detected';

  return (
    <Container>
      <Label>Transcript Language</Label>
      <LanguagePillSelectorWrapper>
        {(['translated', 'original'] as const).map(transcriptOption => {
          const isOriginal = transcriptOption === 'original';
          const button = (
            <LanguagePillSelectorButton
              size="small"
              key={transcriptOption}
              isActive={selectedLanguage === transcriptOption}
              onClick={() => handleChange(transcriptOption)}
              noMargin
              btnType="secondary"
              dataName={`language-option-${transcriptOption}`}
            >
              <Text>
                {transcriptOption === 'translated' ? 'English' : 'Original'}
              </Text>
            </LanguagePillSelectorButton>
          );

          return isOriginal ? (
            <TooltipWrapper key={transcriptOption}>
              <TooltipTrigger>
                {button}
                <TooltipContent>
                  <Text size="small">{languagesText}</Text>
                </TooltipContent>
              </TooltipTrigger>
            </TooltipWrapper>
          ) : (
            <Box key={transcriptOption} flex={1}>
              {button}
            </Box>
          );
        })}
      </LanguagePillSelectorWrapper>
    </Container>
  );
};

export default LanguagePillSelectorV2;
